/**
 * Manabun認証API
 *
 * ManabunからCookieまたはURLパラメータで渡されるトークンを使用して、
 * バックエンド経由でManabun APIからユーザー情報を取得します。
 *
 * 【Manabunからトークンの渡し方】
 * 1. Cookie（Webブラウザ）
 *    - key: rtltoken
 *    - バックエンドで Cookie を読み取って Manabun API を呼ぶ
 *    - 有効期限: 90分
 *    - Domain: .foresight.jp（サブドメイン間で共有）
 *    - Path: /
 *
 * 2. GETクエリパラメータ（スマホアプリ）
 *    - パラメータ名: rtltkn
 *    - 値: ユーザーショートコードをAES128で暗号化したもの
 *    - 例: https://foresight-dev.foresight.jp/{テナント名}/lecture/{講義ID}/?rtltkn={暗号化トークン}
 *    - フロントエンドがURLから抽出しバックエンドに転送する
 *
 * 【バックエンドAPI呼び出し】
 * - メソッド: GET
 * - エンドポイント: /api/v1/auth/end_user/{tenantSlug}/elive
 * - クエリパラメータ: rtltkn（スマホアプリ経由の場合のみ付与）
 * - Cookie は同一ドメインのため自動付与される
 */

import type { ManabunUserResponse, ManabunAuthError } from '../types/manabunUser';

const URL_PARAM_RTL_TOKEN = 'rtltkn';

const END_USER_DOMAIN = import.meta.env.VITE_END_USER_DOMAIN || '';

// ============================================================
// [DEV ONLY] 開発用セッションストレージフォールバック
// Manabun側との疎通が完了したら以下の2関数ごと削除してください。
// authenticateManabunUser() の呼び出し箇所には影響しません。
// ============================================================
const DEV_SESSION_KEY = '__dev_manabun_user__';

/**
 * [DEV ONLY] 取得したユーザー情報をセッションストレージに保存
 *
 * 開発ビルド（import.meta.env.DEV）でのみ動作する。
 * 本番ビルドではビルド時に定数畳み込みされ、処理ごと除去される（S8対応）。
 */
function devSaveUserToSession(user: ManabunUserResponse): void {
  if (!import.meta.env.DEV) return;
  try {
    sessionStorage.setItem(DEV_SESSION_KEY, JSON.stringify(user));
    console.log('[ManabunAuth][DEV] ユーザー情報をセッションストレージに保存しました');
  } catch {
    // sessionStorage が使えない環境ではスキップ
  }
}

/**
 * [DEV ONLY] セッションストレージからユーザー情報を復元
 * バックエンドからトークン情報が取得できなかった場合に使用されます。
 *
 * 開発ビルド（import.meta.env.DEV）でのみ動作する。
 * 本番ビルドでは常に null を返し、認証フォールバックとして機能しない
 * （トークン失効後にセッションが継続してしまう問題の防止。S8対応）。
 */
function devLoadUserFromSession(): ManabunUserResponse | null {
  if (!import.meta.env.DEV) return null;
  try {
    const raw = sessionStorage.getItem(DEV_SESSION_KEY);
    if (!raw) return null;
    const user = JSON.parse(raw) as ManabunUserResponse;
    if (!user?.USER?.USER_ID) return null;
    console.warn('[ManabunAuth][DEV] バックエンドが no_token を返したため、セッションストレージのキャッシュを使用します（開発用）');
    return user;
  } catch {
    return null;
  }
}
// ============================================================

/**
 * URLパラメータから rtltkn を取得し、URLから削除する（スマホアプリ向け）
 *
 * - 値が存在すればトークン文字列を返す
 * - 存在しなければ null を返す
 * - URLからパラメータを除去してブラウザ履歴を更新する（セキュリティのため）
 */
function extractRtltknFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  const token = params.get(URL_PARAM_RTL_TOKEN);

  if (!token) return null;

  console.log('[ManabunAuth] URLパラメータ（rtltkn）からトークンを取得しました');

  // URLからパラメータを削除（セキュリティのため）
  params.delete(URL_PARAM_RTL_TOKEN);
  const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
  window.history.replaceState({}, '', newUrl);

  return token;
}

/**
 * バックエンド経由で Manabun ユーザー情報を取得
 *
 * GET /api/v1/auth/end_user/{tenantSlug}/elive
 */
async function fetchUserViaBackend(
  tenantSlug: string,
  rtltkn: string | null
): Promise<ManabunUserResponse> {
  // rtltkn は AES 暗号化トークンで `+` `/` `=` を含み得るため必ずエンコードする
  // （未エンコードだと `+` がサーバー側で空白にデコードされ、特定トークンだけ認証に失敗する。S9対応）
  const query = rtltkn ? `?rtltkn=${encodeURIComponent(rtltkn)}` : '';
  const url = `${END_USER_DOMAIN}/api/v1/auth/end_user/${tenantSlug}/elive${query}`;

  console.log('[ManabunAuth] バックエンドAPI呼び出し:', `${END_USER_DOMAIN}/api/v1/auth/end_user/${tenantSlug}/elive`);

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({})) as { type?: string; message?: string };
    const error: ManabunAuthError = {
      type: (errorData.type as ManabunAuthError['type']) ?? 'api_error',
      message: errorData.message ?? `エラー: ${response.status} ${response.statusText}`,
    };
    console.error('[ManabunAuth] バックエンドAPIエラー:', error);
    throw error;
  }

  const data = await response.json() as ManabunUserResponse;

  if (!data.USER?.USER_ID) {
    const error: ManabunAuthError = {
      type: 'invalid_response',
      message: '不正なレスポンス形式です',
    };
    console.error('[ManabunAuth] レスポンス検証失敗:', error);
    throw error;
  }

  return data;
}

/**
 * Manabunユーザー認証を実行
 *
 * 【動作フロー】
 * 1. URLパラメータ（rtltkn）を確認（スマホアプリ向け）
 * 2. バックエンドAPI /api/v1/auth/end_user/{tenantSlug}/elive を呼ぶ
 *    - Webブラウザ: バックエンドが rtltoken Cookie を読み取って Manabun API を呼ぶ
 *    - スマホアプリ: rtltkn をクエリパラメータとしてバックエンドに渡す
 * 3. ユーザー情報を返す
 */
export async function authenticateManabunUser(tenantSlug: string): Promise<ManabunUserResponse> {
  console.log('[ManabunAuth] 認証処理を開始します（テナント:', tenantSlug, '）');

  // URLパラメータから rtltkn を取得（スマホアプリの場合のみ値が入る）
  const rtltkn = extractRtltknFromUrl();

  try {
    const user = await fetchUserViaBackend(tenantSlug, rtltkn);

    console.log('[ManabunAuth] ユーザー情報を取得しました:', {
      userId: user.USER.USER_ID,
      nickname: user.USER.USER_NICKNAME,
      courses: user.USER.COURSES.length,
    });

    // [DEV ONLY] 取得したユーザー情報を保存（削除時はこの1行ごと削除）
    devSaveUserToSession(user);

    return user;

  } catch (err) {
    const authError = err as ManabunAuthError;

    // [DEV ONLY] トークン情報が取得できなかった場合、セッションストレージにキャッシュがあれば代替（削除時はこのブロックごと削除）
    const cachedUser = devLoadUserFromSession();
    if (cachedUser) return cachedUser;

    throw authError;
  }
}

/**
 * ユーザー種別
 * - staff: 社員ユーザー（USER_IDの先頭が2）
 * - trial: 試用ユーザー（USER_TRIAL_FLAG=1 かつ USER_VALID=1）
 * - regular: 本受講ユーザー（USER_TRIAL_FLAG=0 かつ USER_VALID=1）
 */
export type UserType = 'staff' | 'trial' | 'regular';

/**
 * ManabunユーザーのユーザーInfoからユーザー種別を判定
 */
export function getUserType(user: ManabunUserResponse): UserType {
  const userIdStr = String(user.USER.USER_ID);
  if (userIdStr.startsWith('2')) {
    return 'staff';
  }

  if (user.USER.USER_VALID === 1) {
    const hasTrial = user.USER.COURSES.some(c => c.USER_TRIAL_FLAG === 1);
    if (hasTrial) return 'trial';
  }

  return 'regular';
}

/**
 * ユーザーが試用ユーザーかどうかを判定（講座指定）
 */
export function isTrialUser(user: ManabunUserResponse, kozaId: number): boolean {
  const course = user.USER.COURSES.find(c => c.COURSE_KOZA_ID === kozaId);
  return course?.USER_TRIAL_FLAG === 1;
}

/**
 * ユーザーが指定した講座・年・月の講義にアクセス権があるかどうかを判定
 */
export function hasCourseAccess(
  user: ManabunUserResponse,
  courseId: number,
  yearMonths: number[]
): boolean {
  const matchingCourses = user.USER.COURSES.filter(
    (c) => c.COURSE_KOZA_ID === courseId
  );

  if (matchingCourses.length === 0) {
    return false;
  }

  return matchingCourses.some((c) => {
    const yearMonth = c.COURSE_YEAR * 100 + c.COURSE_MONTH;
    return yearMonths.includes(yearMonth);
  });
}
